import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PaperButtonComponent, PaperKbdComponent } from '@ricoh-paper/angular';
import { UserPreferencesService } from '../../../../../core/services/user-preferences.service';

/**
 * Command K — the first-run coach mark.
 *
 * RESPONSIBILITY
 * One line of keyboard tips under the scope strip, shown until the user
 * dismisses it. Nothing else in the palette teaches the three chords that make
 * it worth using — `?`, held ⌘, and ⇧↵ — and the footer only has room for the
 * ones that apply to the current selection.
 *
 * WHY IT DISMISSES PERMANENTLY, NOT ON A TIMER
 * A band that vanishes on its own is a band you cannot finish reading. It
 * costs one click to dismiss and the flag lives in `UserPreferences`, so it
 * survives a reload and syncs with the rest of the profile rather than being a
 * private localStorage key nobody else can see.
 *
 * AI CONTEXT
 * `aria-hidden` on the tip text: every chord it names is already announced by
 * the footer's hints and the dialog's own label, so a screen reader would hear
 * the same three shortcuts twice. The dismiss button stays reachable and
 * carries the accessible name.
 */
@Component({
  selector: 'im-cmdk-coach-mark',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperKbdComponent, PaperButtonComponent],
  templateUrl: './cmdk-coach-mark.component.html',
  styleUrl: './cmdk-coach-mark.component.scss',
  host: { '[attr.data-testid]': '"cmdk-coach-mark"' },
})
export class CmdkCoachMarkComponent {
  private readonly prefs = inject(UserPreferencesService);
  private readonly transloco = inject(TranslocoService);

  readonly dismissed = this.prefs.cmdkCoachMarkSeen;

  protected readonly lead = computed(() => this.transloco.translate('shell.cmdk.coach.lead'));
  protected readonly shortcuts = computed(() => this.transloco.translate('shell.cmdk.coach.shortcuts'));
  protected readonly quickOpen = computed(() => this.transloco.translate('shell.cmdk.coach.quick_open'));
  protected readonly book = computed(() => this.transloco.translate('shell.cmdk.coach.book'));
  protected readonly dismissLabel = computed(() => this.transloco.translate('shell.cmdk.coach.dismiss'));

  protected dismiss(): void {
    this.prefs.update({ cmdkCoachMarkSeen: true });
  }
}
